import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <SEOHead
          title="文章未找到 - 萝莉风格时尚穿搭指南"
          description="该文章不存在或已被移除"
          keywords="萝莉,萝莉风格穿搭"
          canonicalUrl={`https://lolitastyle.cn/article/${slug}`}
        />
        <div className="pt-16">
          <div className="max-w-3xl mx-auto px-4 py-16 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">返回首页</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      "@type": "Organization",
      name: "萝莉风格时尚穿搭指南",
    },
  };

  return (
    <Layout>
      <SEOHead
        title={`${article.title} - 萝莉风格时尚穿搭指南`}
        description={article.excerpt.substring(0, 160)}
        keywords={`${article.tags.join(",")},萝莉,萝莉风格穿搭,洛丽塔时尚`}
        canonicalUrl={`https://lolitastyle.cn/article/${slug}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.date}
      />

      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: article.categoryName, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        {/* Article Header */}
        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              {article.categoryName}
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                </div>
                <span>{article.author}</span>
              </div>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </article>

        {/* Cover Image */}
        <div className="w-full px-4 md:px-6 mb-8">
          <div className="max-w-4xl mx-auto relative h-56 md:h-96 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-3xl mx-auto">
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                prose-p:my-3 prose-p:leading-relaxed
                prose-strong:text-foreground-900
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}